import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Users, Search, Trash2, Eye, Edit2, DollarSign, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import { adaptNcf, isElectronicMode, CLIENT_NCF_OPTIONS } from '../utils/ncf';

const Customers = ({ user }) => {
  const [customers, setCustomers] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creditStatusDialogOpen, setCreditStatusDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [rncLookupLoading, setRncLookupLoading] = useState(false);
  const [rncPreview, setRncPreview] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    cedula_rnc: '',
    telefono: '',
    email: '',
    direccion: '',
    tipo_cliente: 'regular',
    tiene_credito: false,
    limite_credito: 0,
    dias_credito: 30,
    tipo_ncf_predeterminado: 'B02',
    lista_precios_id: '',
    notas: '',
  });
  const [electronic, setElectronic] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchPriceLists();
    axios.get(`${API}/business-config`).then(r => setElectronic(isElectronicMode(r.data))).catch(() => {});
  }, []);

  // Consulta RNC en DGII (vía Facturazo) y rellena automáticamente el nombre
  const handleRncLookup = async () => {
    const rnc = (formData.cedula_rnc || '').trim().replace(/-/g, '');
    if (!rnc || rnc.length < 9) {
      toast.error('Ingrese un RNC/Cédula válido (mínimo 9 dígitos)');
      return;
    }
    setRncLookupLoading(true);
    setRncPreview(null);
    try {
      const res = await axios.get(`${API}/rnc/lookup`, { params: { rnc } });
      if (res.data.found) {
        setRncPreview({ name: res.data.name, status: res.data.status });
      } else {
        toast.error('No se encontró el RNC/Cédula en DGII');
      }
    } catch {
      toast.error('Error al consultar DGII');
    } finally {
      setRncLookupLoading(false);
    }
  };

  const confirmRncName = () => {
    if (!rncPreview) return;
    const updates = { nombre: rncPreview.name };
    // Si está activo y RNC válido (9 dígitos), proponer NCF B01 (Crédito Fiscal)
    const clean = (formData.cedula_rnc || '').replace(/\D/g, '');
    if (rncPreview.status === 'ACTIVO' && clean.length === 9) {
      updates.tipo_ncf_predeterminado = adaptNcf('B01', electronic);
    }
    setFormData(prev => ({ ...prev, ...updates }));
    setRncPreview(null);
    toast.success('Nombre del cliente cargado desde DGII');
  };

  const rejectRncName = () => setRncPreview(null);

  const fetchPriceLists = async () => {
    try {
      const response = await axios.get(`${API}/price-lists`);
      setPriceLists(response.data);
    } catch (error) {console.error('Error al cargar listas de precios:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditStatus = async (clientId) => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/credit-status`);
      setCreditStatus(response.data);
      setCreditStatusDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar estado de crédito');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert string values to appropriate types
      const submitData = {
        ...formData,
        limite_credito: parseFloat(formData.limite_credito) || 0,
        dias_credito: parseInt(formData.dias_credito) || 30,
        tiene_credito: formData.tiene_credito === true || formData.tiene_credito === 'true',
        email: formData.email || null, // Permitir null para email
      };

      if (editingCustomer) {
        await axios.patch(`${API}/clients/${editingCustomer.id}`, submitData);
        toast.success('Cliente actualizado exitosamente');
      } else {
        await axios.post(`${API}/clients`, submitData);
        toast.success('Cliente creado exitosamente');
      }
      
      fetchCustomers();
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cliente');
    }
  };

  const handleEdit = (customer) => {
    setRncPreview(null);
    setEditingCustomer(customer);
    setFormData({
      nombre: customer.nombre,
      cedula_rnc: customer.cedula_rnc || customer.rnc || customer.cedula || '',
      telefono: customer.telefono || '',
      email: customer.email || '',
      direccion: customer.direccion || '',
      tipo_cliente: customer.tipo_cliente,
      tiene_credito: customer.tiene_credito,
      limite_credito: customer.limite_credito,
      dias_credito: customer.dias_credito || 30,
      tipo_ncf_predeterminado: customer.tipo_ncf_predeterminado,
      lista_precios_id: customer.lista_precios_id || '',
      notas: customer.notas || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('¿Está seguro de eliminar este cliente?')) return;
    
    try {
      await axios.delete(`${API}/clients/${customerId}`);
      toast.success('Cliente eliminado exitosamente');
      fetchCustomers();
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const handleToggleActive = async (customerId, currentStatus) => {
    try {
      await axios.patch(`${API}/clients/${customerId}`, {
        is_active: !currentStatus
      });
      toast.success(currentStatus ? 'Cliente desactivado' : 'Cliente activado');
      fetchCustomers();
    } catch (error) {
      toast.error('Error al cambiar estado del cliente');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      cedula_rnc: '',
      telefono: '',
      email: '',
      direccion: '',
      tipo_cliente: 'regular',
      tiene_credito: false,
      limite_credito: 0,
      tipo_ncf_predeterminado: 'B02',
      lista_precios_id: '',
      notas: '',
    });
    setEditingCustomer(null);
    setRncPreview(null);
  };

  const filteredCustomers = customers.filter((customer) => {
    // Filtro por estado activo/inactivo
    const isActive = customer.is_active !== false; // Por defecto true si no existe el campo
    if (!showInactive && !isActive) {
      return false;
    }
    
    // Filtro por búsqueda
    return (
      customer.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.cedula_rnc && customer.cedula_rnc.includes(searchTerm)) ||
      (customer.rnc && customer.rnc.includes(searchTerm)) ||
      (customer.cedula && customer.cedula.includes(searchTerm)) ||
      (customer.telefono && customer.telefono.includes(searchTerm))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg">Cargando clientes...</div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="h-6 w-6 md:h-8 md:w-8 text-emerald-600" />
            Gestión de Clientes
          </h1>
          <p className="text-xs md:text-sm text-slate-600 mt-1">
            Administra tus clientes y control de crédito
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
              <Users className="mr-2 h-4 w-4" />
              Nuevo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="customer-form-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
              </DialogTitle>
              <DialogDescription>
                {editingCustomer
                  ? 'Actualiza la información del cliente'
                  : 'Ingresa los datos del nuevo cliente'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Cédula/RNC con consulta DGII al inicio (ancho completo) */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                <Label htmlFor="cedula_rnc" className="text-sm font-semibold">
                  Cédula/RNC <span className="text-xs text-slate-500 font-normal">(consulta automática en DGII)</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="cedula_rnc"
                    value={formData.cedula_rnc}
                    onChange={(e) => { setFormData({ ...formData, cedula_rnc: e.target.value }); setRncPreview(null); }}
                    placeholder="Ej: 001-1234567-8 o 123456789"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleRncLookup(); } }}
                    className="flex-1"
                    data-testid="customer-cedula-rnc-input"
                  />
                  <button
                    type="button"
                    onClick={handleRncLookup}
                    disabled={rncLookupLoading || !formData.cedula_rnc?.trim()}
                    className="h-10 px-4 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white flex items-center gap-2 text-sm font-semibold transition-colors shrink-0"
                    title="Consultar nombre en DGII"
                    data-testid="customer-rnc-lookup-btn"
                  >
                    {rncLookupLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                    Consultar DGII
                  </button>
                </div>
                {rncPreview && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="customer-rnc-preview">
                    <p className="text-[10px] tracking-[0.1em] uppercase font-bold text-emerald-600 mb-0.5">RNC encontrado en DGII</p>
                    <p className="text-sm font-bold text-slate-900 leading-tight">{rncPreview.name}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Estado: {rncPreview.status}</p>
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        onClick={confirmRncName}
                        className="flex-1 h-8 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors"
                        data-testid="customer-rnc-confirm-btn"
                      >
                        Usar este nombre
                      </button>
                      <button
                        type="button"
                        onClick={rejectRncName}
                        className="flex-1 h-8 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-bold transition-colors"
                        data-testid="customer-rnc-reject-btn"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Layout horizontal en dos columnas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* ── Columna Izquierda: Identidad y Contacto ── */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider border-b pb-1">
                    Identidad y Contacto
                  </h3>

                  <div className="space-y-2">
                    <Label htmlFor="nombre">Nombre *</Label>
                    <Input
                      id="nombre"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      placeholder="Nombre del cliente"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tipo_cliente">Tipo de Cliente</Label>
                    <Select
                      value={formData.tipo_cliente}
                      onValueChange={(value) => setFormData({ ...formData, tipo_cliente: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regular">Regular</SelectItem>
                        <SelectItem value="vip">VIP</SelectItem>
                        <SelectItem value="mayorista">Mayorista</SelectItem>
                        <SelectItem value="corporativo">Corporativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="telefono">Teléfono</Label>
                      <Input
                        id="telefono"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        placeholder="809-555-1234"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email <span className="text-xs text-slate-400 font-normal">(opcional)</span></Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="correo@ejemplo.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="direccion">Dirección</Label>
                    <Input
                      id="direccion"
                      value={formData.direccion}
                      onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                      placeholder="Dirección completa"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notas">Notas</Label>
                    <textarea
                      id="notas"
                      value={formData.notas}
                      onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                      placeholder="Notas adicionales sobre el cliente"
                      rows={3}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* ── Columna Derecha: Configuración Fiscal y Crédito ── */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider border-b pb-1">
                    Configuración Fiscal y Crédito
                  </h3>

                  <div className="space-y-2">
                    <Label htmlFor="tipo_ncf_predeterminado">Tipo de NCF Predeterminado</Label>
                    <Select
                      value={adaptNcf(formData.tipo_ncf_predeterminado, electronic)}
                      onValueChange={(value) => setFormData({ ...formData, tipo_ncf_predeterminado: value })}
                    >
                      <SelectTrigger data-testid="customer-ncf-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CLIENT_NCF_OPTIONS[electronic ? 'E' : 'B'].map(o => (
                          <SelectItem key={o.code} value={o.code}>{o.code} - {o.description}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lista_precios">Lista de Precios</Label>
                    <Select
                      value={formData.lista_precios_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, lista_precios_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione una lista (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin lista asignada (usar General)</SelectItem>
                        {priceLists.filter(list => list.activa).map((list) => (
                          <SelectItem key={list.id} value={list.id}>
                            {list.nombre}
                            {list.es_general && ' (Por defecto)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Si no se selecciona, se usará la lista "General"
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="tiene_credito"
                        checked={formData.tiene_credito}
                        onChange={(e) => setFormData({ ...formData, tiene_credito: e.target.checked })}
                        className="rounded h-4 w-4"
                      />
                      <Label htmlFor="tiene_credito" className="cursor-pointer font-semibold text-amber-900">
                        Habilitar crédito al cliente
                      </Label>
                    </div>

                    {formData.tiene_credito && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="limite_credito">Límite de Crédito (RD$)</Label>
                          <Input
                            id="limite_credito"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.limite_credito}
                            onChange={(e) => setFormData({ ...formData, limite_credito: e.target.value })}
                            placeholder="0.00"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dias_credito">Días de Crédito</Label>
                          <Input
                            id="dias_credito"
                            type="number"
                            min="1"
                            value={formData.dias_credito}
                            onChange={(e) => setFormData({ ...formData, dias_credito: e.target.value })}
                            placeholder="30"
                            required
                          />
                        </div>
                      </div>
                    )}
                    {!formData.tiene_credito && (
                      <p className="text-xs text-amber-700">
                        Activa esta opción para asignar un límite de crédito y días de gracia.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  {editingCustomer ? 'Actualizar' : 'Crear'} Cliente
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar and Filters */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Buscar por nombre, cédula/RNC o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Filtro de clientes inactivos */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="showInactive"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-slate-300"
          />
          <Label htmlFor="showInactive" className="text-sm text-slate-600 cursor-pointer">
            Mostrar clientes inactivos
          </Label>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">
                  Cédula/RNC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                  Contacto
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">
                  Lista de Precios
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell">
                  Estado
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Crédito
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-slate-50">
                  {/* Columna 1: Código */}
                  <td className="px-4 py-3">
                    <div className="text-sm font-mono text-slate-600">
                      {customer.codigo_cliente || 'N/A'}
                    </div>
                  </td>
                  
                  {/* Columna 2: Cliente (sin negrita) */}
                  <td className="px-4 py-3">
                    <div>
                      <div className="text-sm text-slate-900">{customer.nombre}</div>
                      <div className="text-xs text-slate-500 md:hidden">
                        {customer.cedula_rnc || customer.rnc || customer.cedula}
                      </div>
                      <div className="text-xs text-slate-500 lg:hidden">
                        {customer.telefono}
                      </div>
                    </div>
                  </td>
                  
                  {/* Columna 3: Cédula/RNC */}
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="text-sm text-slate-900">
                      {customer.cedula_rnc || customer.rnc || customer.cedula || 'N/A'}
                    </div>
                  </td>
                  
                  {/* Columna 4: Contacto */}
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="text-sm text-slate-900">
                      {customer.telefono && <div>{customer.telefono}</div>}
                      {customer.email && <div className="text-xs text-slate-500">{customer.email}</div>}
                    </div>
                  </td>
                  
                  {/* Columna 5: Lista de Precios */}
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="text-sm text-slate-600">
                      {customer.lista_precios_id ? (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                          {priceLists.find(l => l.id === customer.lista_precios_id)?.nombre || 'Lista Personalizada'}
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded">
                          General
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      {customer.tipo_cliente}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {customer.is_active !== false ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-800 font-medium">
                        Activo
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600 font-medium">
                        Inactivo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {customer.tiene_credito ? (
                      <div>
                        <div className="text-sm font-medium text-emerald-600">
                          {formatCurrency(customer.credito_disponible)}
                        </div>
                        <div className="text-xs text-slate-500">
                          de {formatCurrency(customer.limite_credito)}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchCreditStatus(customer.id)}
                          className="mt-1 p-0 h-auto text-xs text-blue-600 hover:text-blue-800"
                        >
                          <DollarSign className="h-3 w-3 mr-1" />
                          Ver estado
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Sin crédito</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(customer)}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        title="Editar cliente"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(customer.id, customer.is_active !== false)}
                        className={customer.is_active !== false ? 
                          "text-slate-600 hover:text-slate-800 hover:bg-slate-50" : 
                          "text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                        }
                        title={customer.is_active !== false ? "Desactivar cliente" : "Activar cliente"}
                      >
                        {customer.is_active !== false ? (
                          <XCircle className="h-4 w-4" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                      </Button>
                      {(user?.role === 'superadmin' || user?.role === 'admin') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(customer.id)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          title="Eliminar cliente"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCustomers.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            No se encontraron clientes
          </div>
        )}
      </div>

      {/* Credit Status Dialog */}
      <Dialog open={creditStatusDialogOpen} onOpenChange={setCreditStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Estado de Crédito</DialogTitle>
            <DialogDescription>
              Información detallada del crédito del cliente
            </DialogDescription>
          </DialogHeader>
          {creditStatus && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{creditStatus.nombre}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600">Límite de Crédito</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {formatCurrency(creditStatus.limite_credito)}
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-slate-600">Crédito Disponible</p>
                  <p className="text-lg font-semibold text-emerald-600">
                    {formatCurrency(creditStatus.credito_disponible)}
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs text-slate-600">Deuda Actual</p>
                  <p className="text-lg font-semibold text-red-600">
                    {formatCurrency(creditStatus.deuda_actual)}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-slate-600">Estado</p>
                  <p className="text-lg font-semibold">
                    {creditStatus.puede_comprar_credito ? (
                      <span className="text-emerald-600">Disponible</span>
                    ) : (
                      <span className="text-red-600">No disponible</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={() => setCreditStatusDialogOpen(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
